import { httpClientInterface } from 'app-components/utils/core';
import { getServiceUrl } from 'app-components/utils/core/interfaces/httpClientInterface';
import { filteredArraySchema } from 'app-components/utils/zodHelpers';
import z from 'zod';

import type { RetirementStorageData } from './retirementPlannerTypes';
import { RetirementStorageDataSchema } from './retirementPlannerTypes';
import { convertAllToString } from './retirementPlannerUtils';

const stringifyNumbersInRetirementData = (data: any) => {
  if (!data?.resources) return data;
  const newData = data.resources.map((resource: any) => ({
    ...resource,
    data: {
      ...resource.data,
      formDataAdvanced: convertAllToString(resource.data.formDataAdvanced),
      formDataBasic: convertAllToString(resource.data.formDataBasic),
    },
  }));
  return { resources: newData };
};

const RemoteRetirementResourceSchema = RetirementStorageDataSchema.and(z.object({ id: z.string() }));

const GetRetirementPlannerResponseSchema = z.object({
  resources: filteredArraySchema(RemoteRetirementResourceSchema),
});

const PostRetirementPlannerResponseSchema = z.object({
  resource: RemoteRetirementResourceSchema,
  id: z.string(),
  clientId: z.string().optional(),
});
export type PostRetirementPlannerResponseType = z.infer<typeof PostRetirementPlannerResponseSchema>;

const PutRetirementPlannerResponseSchema = z.object({
  resource: RemoteRetirementResourceSchema,
  id: z.string(),
});
export type PutRetirementPlannerResponseType = z.infer<typeof PutRetirementPlannerResponseSchema>;

export default {
  async create(resource: RetirementStorageData & { clientId: string }) {
    const requestUrl = `${getServiceUrl()}/wildcard/retirement-planner`;
    const response = await httpClientInterface.send('POST', requestUrl, resource);
    return PostRetirementPlannerResponseSchema.parse(response.data);
  },
  async fetch() {
    const requestUrl = `${getServiceUrl()}/wildcard/retirement-planner`;
    const response = await httpClientInterface.send('GET', requestUrl);
    //
    // FIX LEGACY DATA
    // THe initial version of retirement-planner used a possible mix of numbers and strings for
    // all fields.  We incrementally fix this here by ensuring those fields are converted to strings before
    // we send them to the schema filter
    //
    const responseDataCorrected = stringifyNumbersInRetirementData(response.data);
    return GetRetirementPlannerResponseSchema.parse(responseDataCorrected).resources;
  },
  async update(resource: RetirementStorageData & { id: string }) {
    const requestUrl = `${getServiceUrl()}/wildcard/retirement-planner/${resource.id}`;
    const response = await httpClientInterface.send('PUT', requestUrl, resource);
    return PutRetirementPlannerResponseSchema.parse(response.data);
  },
  async delete(id: string) {
    const requestUrl = `${getServiceUrl()}/wildcard/retirement-planner/${id}`;
    await httpClientInterface.send('DELETE', requestUrl);
  },
} as const;
